import { Body4 } from '@components/typography/Body4.tsx';
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
} from 'react';
import { TextInput } from '@components/input/TextInput.tsx';
import {
  ValidationMode,
  ValidationResult,
} from '@funnellab/shared/src/validation.ts';
import { usePromise } from '@hooks/usePromise.ts';
import { useTranslation } from 'react-i18next';
import Combobox from '@components/input/Combobox.tsx';
import countryService, {
  type Country,
} from '@src/api/services/CountryService.ts';
import { useValidation } from '@src/hooks/validation/useValidation';
import { validVatNo } from '@src/hooks/validation/rules/validVatNo';
import { required } from '@src/hooks/validation/rules/required';

export type CreateOrganizationType = {
  name: string;
  street?: string;
  city?: string;
  zip?: string;
  country?: Country;
  vatNo?: string;
  user_created: string;
};

export function CreateOrganizationForm({
  organizationData,
  setOrganizationData,
  setFormIsValid,
}: {
  organizationData: Partial<CreateOrganizationType>;
  setOrganizationData: Dispatch<
    SetStateAction<Partial<CreateOrganizationType>>
  >;
  setFormIsValid: Dispatch<SetStateAction<boolean>>;
}) {
  const { t } = useTranslation('organizationDetails');

  const { data: countries } = usePromise(
    async () => countryService.getCountries(),
    { setLoadingStateOnRefresh: true },
    [],
  );

  const setValue = useCallback(
    <Field extends keyof CreateOrganizationType>(
      fieldIdentifier: Field,
      newValue: CreateOrganizationType[Field] | null | undefined,
    ) => {
      setOrganizationData((prev) => ({
        ...prev,
        [fieldIdentifier]: newValue,
      }));
    },
    [setOrganizationData],
  );

  const vatNoValidation = useValidation(
    organizationData.vatNo ?? '',
    organizationData.country?.subdivision_code
      ? [validVatNo(organizationData.country.subdivision_code)]
      : [],
    undefined,
  );
  const orgNameValidation = useValidation(
    organizationData.name ?? '',
    [required],
    undefined,
  );

  useEffect(() => {
    setFormIsValid(vatNoValidation.isValid && orgNameValidation.isValid);
  }, [setFormIsValid, vatNoValidation.isValid, orgNameValidation.isValid]);

  return (
    <>
      <TextInput<string>
        value={organizationData.name ?? ''}
        title={t('name')}
        inputTestId="organization-name-input"
        TitleComponent={Body4}
        placeholder={t('name')}
        onChange={(input) => setValue('name', input)}
        required
        validation={orgNameValidation}
      />
      <TextInput<string>
        value={organizationData.street ?? ''}
        title={t('street')}
        inputTestId="organization-street-input"
        TitleComponent={Body4}
        placeholder={t('street')}
        onChange={(input) => setValue('street', input)}
        validationMode={ValidationMode.ON_BLUR}
        validate={(street) =>
          street ? ValidationResult.VALID : ValidationResult.EMPTY
        }
      />
      <div className="flex flex-wrap gap-x-6">
        <TextInput<string>
          value={organizationData.city ?? ''}
          title={t('city')}
          inputTestId="organization-city-input"
          TitleComponent={Body4}
          placeholder={t('city')}
          onChange={(input) => setValue('city', input)}
          validationMode={ValidationMode.ON_BLUR}
          validate={(city) =>
            city ? ValidationResult.VALID : ValidationResult.EMPTY
          }
          classNameContainer="flex-1 min-w-40"
        />
        <TextInput<string>
          value={organizationData.zip ?? ''}
          title={t('zipCode')}
          inputTestId="organization-zip-input"
          TitleComponent={Body4}
          placeholder={t('zipCode')}
          onChange={(input) => setValue('zip', input)}
          validationMode={ValidationMode.ON_BLUR}
          validate={(zip) =>
            zip ? ValidationResult.VALID : ValidationResult.EMPTY
          }
          classNameContainer="flex-1 min-w-40"
        />
      </div>
      <Combobox<Country>
        title={t('country')}
        TitleComponent={Body4}
        placeholder={t('country')}
        options={countries ?? []}
        displayValue={(value) => value.label ?? ''}
        value={organizationData.country ?? null}
        onChange={(country) => setValue('country', country)}
        showDropdownButton
        leadingIcon={null}
        classNameContainer="flex-1 min-w-40"
        validationMode={ValidationMode.ON_BLUR}
        validate={(country) =>
          country ? ValidationResult.VALID : ValidationResult.EMPTY
        }
        classNameLabel="!mb-1"
      />
      <TextInput<string>
        value={organizationData.vatNo ?? ''}
        title={t('vatNo')}
        TitleComponent={Body4}
        placeholder={t('vatNo')}
        onChange={(input) => setValue('vatNo', input)}
        classNameContainer="flex-1 min-w-40"
        disabled={!organizationData.country}
        validation={vatNoValidation}
      />
    </>
  );
}
